import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'react-tooltip';

import type { UrgentCaseDashboardItem } from '@/api/urgent-cases';
import { formatId } from '@/utils/strings';
import { useGetCaseComments } from '@/hooks/api/use-get-case-comments';
import { usePostCaseComment } from '@/hooks/api/use-post-case-comment';
import { usePatchGradeAcknowledge } from '@/hooks/api/use-patch-grade-acknowledge';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { CaseAdminContactsCard } from './case-admin-contacts-card';
import { TrackingHistoryList } from './tracking-history-list';
import { LogContactForm } from './log-contact-form';

interface TrackAcknowledgementSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  urgentCase: UrgentCaseDashboardItem;
  onMutate?: () => void;
}

export function TrackAcknowledgementSheet({
  open,
  onOpenChange,
  urgentCase,
  onMutate,
}: TrackAcknowledgementSheetProps) {
  const { t } = useTranslation();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { comments, mutate: mutateComments } = useGetCaseComments(open ? urgentCase.case_id : null);
  const { isLoading: isPostingComment, submitComment, userEmail } = usePostCaseComment();
  const { isLoading: isAcknowledging, patchGradeAcknowledge } = usePatchGradeAcknowledge();

  const handleLogContact = async (comment: string): Promise<void> => {
    await submitComment(urgentCase.case_id, comment);
    await mutateComments();
  };

  const handleAcknowledgeConfirm = async (e: React.MouseEvent) => {
    e.preventDefault(); // prevent Radix auto-close so the full async chain completes
    await patchGradeAcknowledge(urgentCase.grade_id, urgentCase.case_id);
    setShowConfirmDialog(false);
    onOpenChange(false);
    onMutate?.();
  };

  const handleCheckboxChange = () => {
    setShowConfirmDialog(true);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t('track-ack-title')}</SheetTitle>
            <SheetDescription>
              {t('track-ack-case-patient-description', {
                caseId: formatId(urgentCase.case_id),
                patientId: formatId(urgentCase.patient_id),
              })}
              <Tooltip id="sheet-case-id" place="top">
                {urgentCase.case_id}
              </Tooltip>
              <Tooltip id="sheet-patient-id" place="top">
                {urgentCase.patient_id}
              </Tooltip>
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6 p-4">
            {/* Case Details */}
            <div className="rounded-lg border p-4 bg-muted/50 space-y-2">
              <h4 className="font-semibold">{t('track-ack-case-details')}</h4>
              <div className="text-sm space-y-1">
                <p>
                  <span className="text-muted-foreground">{t('track-ack-case-id-label')}</span>{' '}
                  <span className="font-mono" data-tooltip-id="detail-case-id">
                    {formatId(urgentCase.case_id)}
                  </span>
                  <Tooltip id="detail-case-id" place="top">
                    {urgentCase.case_id}
                  </Tooltip>
                </p>
                <p>
                  <span className="text-muted-foreground">{t('track-ack-patient-label')}</span>{' '}
                  <span data-tooltip-id="detail-patient-id">{formatId(urgentCase.patient_id)}</span>
                  <Tooltip id="detail-patient-id" place="top">
                    {urgentCase.patient_id}
                  </Tooltip>
                </p>
                <p>
                  <span className="text-muted-foreground">{t('track-ack-clinic-label')}</span>{' '}
                  {urgentCase.clinic_name}
                </p>
                <p>
                  <span className="text-muted-foreground">{t('track-ack-created-label')}</span>{' '}
                  {new Date(urgentCase.created_at).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Case Admin Contacts */}
            <CaseAdminContactsCard caseAdmins={urgentCase.case_admins} />

            {/* Suggested Dialog */}
            <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm text-blue-900 dark:text-blue-100">
                  {t('track-ack-suggested-dialog-title')}
                </h4>
              </div>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('track-ack-suggested-dialog-text')}
              </p>
            </div>

            {/* Log Contact Form */}
            {!urgentCase.acknowledged_by && (
              <LogContactForm
                onSubmit={handleLogContact}
                disabled={isAcknowledging || isPostingComment}
              />
            )}

            {/* Tracking History */}
            <TrackingHistoryList
              comments={comments}
              caseId={urgentCase.case_id}
              currentUserEmail={userEmail}
              onMutate={mutateComments}
              readOnly={!!urgentCase.acknowledged_by}
            />

            {/* Final Acknowledgement */}
            <Button
              onClick={handleCheckboxChange}
              disabled={!!urgentCase.acknowledged_by || isAcknowledging}
              className="w-full"
            >
              {urgentCase.acknowledged_by
                ? t('track-ack-case-acknowledged')
                : t('track-ack-mark-as-acknowledged')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('track-ack-confirm-title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('track-ack-confirm-description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleAcknowledgeConfirm}>{t('confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
